import {
  App,
  Button,
  Empty,
  Form,
  Input,
  Menu,
  Modal,
  Skeleton,
  Switch,
  Table,
  theme as antdTheme,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { setupApi, type LookupItem } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const kindsKey = ['setup', 'lookup-kinds'] as const;
const lookupKey = (slug: string) => ['setup', 'lookups', slug, 'all'] as const;

interface FormShape {
  name: string;
}

export function LookupsPage() {
  const { token } = antdTheme.useToken();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const canCreate = useAuthStore((s) => s.hasAuthority('SETUP:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('SETUP:EDIT'));

  const [editing, setEditing] = useState<LookupItem | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: kinds, isLoading: kindsLoading } = useQuery({
    queryKey: kindsKey,
    queryFn: setupApi.listKinds,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!slug && kinds && kinds.length > 0) {
      navigate(`/setup/lookups/${kinds[0]!.slug}`, { replace: true });
    }
  }, [slug, kinds, navigate]);

  const activeKind = kinds?.find((k) => k.slug === slug);

  const { data: rows, isLoading: rowsLoading } = useQuery({
    queryKey: slug ? lookupKey(slug) : ['setup', 'lookups', 'pending'],
    queryFn: () => setupApi.listLookup(slug!, false),
    enabled: !!slug,
  });

  const upsert = useMutation({
    mutationFn: async (values: FormShape) => {
      const name = values.name.trim();
      return editing
        ? setupApi.renameLookup(slug!, editing.id, name)
        : setupApi.createLookup(slug!, name);
    },
    onSuccess: () => {
      message.success(editing ? 'Renamed' : 'Created');
      if (slug) qc.invalidateQueries({ queryKey: lookupKey(slug) });
      setOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      setupApi.toggleLookup(slug!, id, value),
    onSuccess: () => {
      if (slug) qc.invalidateQueries({ queryKey: lookupKey(slug) });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row: LookupItem) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name });
    setOpen(true);
  };

  const columns: TableProps<LookupItem>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit || (toggle.isPending && toggle.variables?.id === row.id)}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Rename
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / General"
        title="Lookup catalogs"
        description={
          activeKind
            ? `Editing the “${activeKind.label}” catalog.`
            : 'Pick a catalog from the list.'
        }
        count={
          slug && rows ? { value: rows.length, label: 'rows' } : undefined
        }
        actions={
          canCreate && slug ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New entry
            </Button>
          ) : null
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 240px) 1fr',
          gap: 18,
          alignItems: 'start',
        }}
      >
        <aside
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusLG,
            background: token.colorBgContainer,
            overflow: 'hidden',
          }}
        >
          {kindsLoading ? (
            <div style={{ padding: 12 }}><Skeleton active paragraph={{ rows: 6 }} /></div>
          ) : (
            <Menu
              mode="inline"
              selectedKeys={slug ? [slug] : []}
              items={(kinds ?? []).map((k) => ({ key: k.slug, label: k.label }))}
              onClick={({ key }) => navigate(`/setup/lookups/${key}`)}
              style={{ borderInlineEnd: 'none', background: 'transparent' }}
            />
          )}
        </aside>

        <section style={{ minWidth: 0 }}>
          {!slug ? (
            <Empty description="Select a catalog" />
          ) : (
            <Table
              rowKey="id"
              size="middle"
              loading={rowsLoading}
              columns={columns}
              dataSource={rows ?? []}
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
            />
          )}
        </section>
      </div>

      <Modal
        title={
          editing
            ? `Rename ${editing.name}`
            : `New ${activeKind?.label.replace(/s$/, '').toLowerCase() ?? 'entry'}`
        }
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(values) => upsert.mutate(values)}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 100 },
            ]}
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
